import React from 'react';
import { Link } from 'react-router-dom';

/**
 * ConversionValueComparison
 *
 * Props:
 * - showUrgency: boolean flag to control showing the crossed-out higher price
 * - paybackMonths: ROI payback period in months
 * - onUpgrade: callback when user chooses the professional report
 */
const ConversionValueComparison = ({ showUrgency, paybackMonths, onUpgrade }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
      {/* Free Preview */}
      <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Free Assessment Preview</h3>
          <div className="text-3xl font-bold text-gray-600">$0</div>
          <p className="text-gray-600">What you get today</p>
        </div>

        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <svg
              className="w-5 h-5 text-green-500 mr-3 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-gray-700">Basic energy assessment results</span>
          </li>
          <li className="flex items-start">
            <svg
              className="w-5 h-5 text-green-500 mr-3 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-gray-700">General savings opportunities</span>
          </li>
          <li className="flex items-start">
            <svg
              className="w-5 h-5 text-green-500 mr-3 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-gray-700">Industry benchmarking</span>
          </li>
          <li className="flex items-start">
            <svg
              className="w-5 h-5 text-red-500 mr-3 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            <span className="text-gray-400">No implementation roadmap</span>
          </li>
          <li className="flex items-start">
            <svg
              className="w-5 h-5 text-red-500 mr-3 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            <span className="text-gray-400">No vendor recommendations</span>
          </li>
          <li className="flex items-start">
            <svg
              className="w-5 h-5 text-red-500 mr-3 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            <span className="text-gray-400">No grant matching</span>
          </li>
        </ul>

        <Link
          to="/my-assessments"
          className="w-full bg-gray-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-700 transition-colors text-center block"
        >
          View Free Results
        </Link>
      </div>

      {/* Professional Report */}
      <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-6 border-2 border-emerald-300 relative">
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-emerald-600 text-white px-4 py-1 rounded-full text-sm font-medium">
            Most Popular
          </span>
        </div>

        <div className="text-center mb-6 mt-4">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Professional Energy Report</h3>
          <div className="flex items-center justify-center space-x-2 mb-2">
            {showUrgency && (
              <span className="text-lg text-gray-500 line-through">$1,190</span>
            )}
            <div className="text-4xl font-bold text-emerald-600">$990</div>
          </div>
          <p className="text-emerald-700 font-medium">
            Pays for itself in {paybackMonths} months
          </p>
        </div>

        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <svg
              className="w-5 h-5 text-emerald-500 mr-3 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-gray-700">
              <strong>Everything in Free</strong> plus:
            </span>
          </li>
          <li className="flex items-start">
            <svg
              className="w-5 h-5 text-emerald-500 mr-3 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-gray-700">Detailed 18-month implementation roadmap</span>
          </li>
          <li className="flex items-start">
            <svg
              className="w-5 h-5 text-emerald-500 mr-3 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-gray-700">
              Prioritized investment recommendations (High/Medium priority)
            </span>
          </li>
          <li className="flex items-start">
            <svg
              className="w-5 h-5 text-emerald-500 mr-3 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-gray-700">Verified vendor & contractor database</span>
          </li>
          <li className="flex items-start">
            <svg
              className="w-5 h-5 text-emerald-500 mr-3 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-gray-700">
              Matched grant opportunities ($125K+ typically available)
            </span>
          </li>
          <li className="flex items-start">
            <svg
              className="w-5 h-5 text-emerald-500 mr-3 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-gray-700">
              Productivity analysis beyond energy ($185K+ additional revenue)
            </span>
          </li>
          <li className="flex items-start">
            <svg
              className="w-5 h-5 text-emerald-500 mr-3 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-gray-700">
              Financial modeling & payback analysis (typically 1-month payback)
            </span>
          </li>
          <li className="flex items-start">
            <svg
              className="w-5 h-5 text-emerald-500 mr-3 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-gray-700">
              Executive presentation ready with implementation timeline
            </span>
          </li>
          <li className="flex items-start">
            <svg
              className="w-5 h-5 text-emerald-500 mr-3 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-gray-700">
              <strong>90-day money-back guarantee</strong>
            </span>
          </li>
        </ul>

        <button
          onClick={onUpgrade}
          className="w-full bg-emerald-600 text-white py-4 px-6 rounded-lg font-bold text-lg hover:bg-emerald-700 transition-colors shadow-lg"
        >
          Get Professional Report - $990
        </button>

        <p className="text-xs text-center text-gray-600 mt-2">
          Secure payment • Delivered within 48 hours • 90-day guarantee
        </p>
      </div>
    </div>
  );
};

export default ConversionValueComparison;


